import React, { useState } from 'react';
import cx from 'classnames';
import moment from 'moment-timezone';

import CardLayout from '@magoosh/layouts/card';
import { InfoAlert } from '@magoosh/lib/alerts';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import ProgressBar from '@magoosh/lib/progress_bar';
import LessonActivity from '@magoosh/organisms/activities/lesson';
import StatusAlert from '@magoosh/organisms/grading_request_status_alert';
import RatingForm from '@magoosh/organisms/rating';
import { paths } from 'config/path_helpers';
import { RatingData } from 'types/rating';
import { pluralize } from 'utilities/pluralize';

import ContextualLink from '@test_prep/app/components/contextual_link';

import ContentCard from './content_card';
import SectionHeader from './section_header';
import SuggestedEdits from './suggested_edits';
import SuggestedEditFilters from './suggested_edits/filters';
import WrittenFeedback from './written_feedback';
import { EditedResponseFilter, Response, ScoreReport } from '../types';

import FeedbackChatBubble from './assets/feedback_chat_bubble.svg';
import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  answer: {
    id: number;
    practiceSession: {
      id: number;
      practiceTargetId: number;
      practiceTargetType: string;
    };
    prompt: {
      content: {
        html: string;
        text: string;
      };
      hasSampleResponses: boolean;
      id: number;
    };
    question: {
      title: string;
    };
  };
  branding: {
    examName: string;
  };
  previousScore: number;
  relatedLessons: {
    lengthInWords: string;
    locked: boolean;
    name: string;
    slug: string;
    status: {
      drills: 'completed';
      video: 'completed' | 'watched';
    };
  }[];
  response: Response;
  rating: RatingData;
  scoreReport: ScoreReport;
}

const WritingScoreReport: React.FC<any> = (props) => {
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [suggestedEditFilters, setSuggestedEditFilters] = useState<EditedResponseFilter[]>([]);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const breadcrumbs = () => {
   if (relatedToPracticeTest()) {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Practice Tests', path: paths.practiceTests() },
        { name: 'Results', path: paths.practiceTest(props.answer.practiceSession.practiceTargetId) },
        { name: 'Score Report' }
      ];
    } else if (props.answer.practiceSession) {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Practice Session', path: paths.answers({ 'review[practice_session_id]': props.answer.practiceSession.id }) },
        { name: 'Answer', path: paths.answer(props.answer.id, { 'review[practice_session_id]': props.answer.practiceSession.id }) },
        { name: 'Score Report' }
      ];
    } else {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Answer', path: paths.answer(props.answer.id) },
        { name: 'Score Report' }
      ];
    }
  };

  const lessonsWatchedCount = () => {
    return props.relatedLessons.filter((lesson) => (
      (lesson as any).status.video === 'watched' || (lesson as any).status.video === 'completed'
    )).length;
  };

  const relatedToPracticeTest = () => {
    return props.answer.practiceSession?.practiceTargetType === 'MockTest';
  };

  const renderTextAsParagraphs = (text: string) => {
    if (!text) return;

    return text.replace(/\r\n/g, '\n').split(/\n+/).map((paragraph, idx) => (
      <p key={idx}>{paragraph}</p>
    ));
  };

  const rubricUrl = () => {
    if (props.branding.examName === 'GRE') {
      return 'https://www.ets.org/gre/test-takers/general-test/prepare/content/analytical-writing/scoring.html';
    } else if (props.branding.examName === 'TOEFL') {
      return 'https://www.ets.org/pdfs/toefl/writing-rubrics.pdf';
    } else {
      return null;
    }
  };

  return (
    <CardLayout breadcrumbs={breadcrumbs()} linkComponent={ContextualLink}>
      <div className="d-flex flex-column gap-m">
        {
          props.scoreReport.status !== 'finished' && (
            <StatusAlert
              answerId={props.answer.id}
              graderType={props.scoreReport.grader.type}
              onFinished={() => window.location.reload() }
              status={props.scoreReport.status}
            />
          )
        }

        <div className="d-flex flex-column flex-lg-row gap-s">
          <div className="d-flex flex-column gap-s flex-1">
            <h1 className="text-gray-darker u-margin-A-n">
              <strong>
                {
                  props.scoreReport.reportType ? (
                    `${props.scoreReport.reportType} Score Report`
                  ) : 'Score Report'
                }
              </strong>
            </h1>

            <div className="d-flex flex-column gap-xs">
              {
                relatedToPracticeTest() ? (
                  <>
                    <div>
                      This response was part of a practice test you completed <strong>{
                        moment(props.response.createdAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                          ? 'Today'
                          : moment(props.response.createdAt * 1000).tz(timeZone).fromNow()
                      }</strong>
                    </div>

                    <div>
                      <ContextualLink
                        className="text-secondary text-hover-no-decoration text-no-decoration"
                        href={paths.practiceTest(props.answer.practiceSession.practiceTargetId)}
                      >
                        <strong>See full practice test score report</strong>
                      </ContextualLink>
                    </div>
                  </>
                ) : (
                  <div>
                    Answered <strong>{
                      moment(props.response.createdAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                        ? 'Today'
                        : moment(props.response.createdAt * 1000).tz(timeZone).fromNow()
                    }</strong>
                  </div>
                )
              }
            </div>
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <CardLayout.Card className={cx(style.scoreCard, 'd-flex flex-column gap-m')}>
              <div>
                <h3 className="u-margin-A-n">
                  Score
                  {(() => {
                    if (props.scoreReport.status === 'finished') {
                      return <>{' '}<strong>{props.scoreReport.score}</strong></>;
                    } else if (props.scoreReport.status === 'error') {
                      // TODO
                      return <>Error</>;
                    } else {
                      return null;
                    }
                  })()}
                </h3>
              </div>

              <div className="align-items-center d-flex gap-s">
                <div className="flex-1" style={{ minWidth: 250 }}>
                  <ProgressBar
                    backgroundColor={colors.grayLighter}
                    color="linear-gradient(90deg, rgb(55, 145, 237), rgb(113, 78, 246))"
                    percentage={props.scoreReport.score ? (props.scoreReport.score / props.scoreReport.scoreRange.max) * 100 : 0}
                    size="md"
                  />
                </div>

                <div className="flex-none small">
                  out of {props.scoreReport.scoreRange.max}
                </div>
              </div>

              {(() => {
                if (!props.previousScore || !props.scoreReport.score) {
                  return null;
                } else if (props.previousScore == props.scoreReport.score) {
                  return (
                    <small>About the same as your last attempt</small>
                  );
                } else if (props.previousScore > props.scoreReport.score) {
                  return (
                    <small>{pluralize(props.previousScore - props.scoreReport.score, 'point')} lower than your last attempt</small>
                  );
                } else {
                  return (
                    <small>🎉 {pluralize(props.scoreReport.score - props.previousScore, 'point')} higher than your last attempt</small>
                  );
                }
              })()}
            </CardLayout.Card>
          </div>
        </div>

        <CardLayout.Card className="d-flex flex-column flex-md-row gap-m gap-lg-xl">
          <SectionHeader
            supertitle="Prompt and response"
            supertitleIcon="file-text"
            title="What you wrote"
          />

          <div className="d-flex flex-1 flex-column gap-m" style={{ minWidth: 0 }}>
            <ContentCard className="d-flex flex-1 flex-column gap-m">
              <div
                className="align-items-center d-flex gap-s"
                onClick={() => setPromptExpanded(!promptExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <div className="flex-1">
                  <strong>Prompt</strong>
                </div>

                <div>
                  <strong>
                    <FontAwesomeIcon icon={promptExpanded ? 'minus' : 'plus'} />
                  </strong>
                </div>
              </div>

              <div className="d-flex flex-column gap-m small">
                {
                  promptExpanded ? (
                    <div
                      className="answer"
                      dangerouslySetInnerHTML={{ __html: props.answer.prompt.content.html }}
                    />
                  ) : (
                    <div
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {props.answer.prompt.content.text}
                    </div>
                  )
                }
              </div>
            </ContentCard>

            <ContentCard className="d-flex flex-1 flex-column gap-m">
              <div className="align-items-center d-flex gap-s">
                <img alt="" src={FeedbackChatBubble} />
                <span className="small">
                  You on {moment.unix(props.response.createdAt).tz(timeZone).format('MMMM D, YYYY')}
                </span>
              </div>

              <div>
                {renderTextAsParagraphs(props.response.content)}
              </div>
            </ContentCard>
          </div>
        </CardLayout.Card>

        <CardLayout.Card className="d-flex flex-column flex-md-row gap-m gap-lg-xl">
          <SectionHeader
            subtitle={<>
              <div className="small">
                This evaluation is based on the {props.branding.examName} scoring rubric.
              </div>

              {
                rubricUrl() && (
                  <div style={{ lineHeight: 1 }}>
                    <a
                      className="text-no-decoration text-secondary"
                      href={rubricUrl()}
                      style={{ fontSize: 12 }}
                      target="_blank"
                    >
                      <strong>See the scoring rubric</strong>
                    </a>
                  </div>
                )
              }
            </>}
            supertitle="Feedback"
            supertitleIcon="commenting"
            title="Your score explained"
          />

          <div className="d-flex flex-1 flex-column gap-m" style={{ minWidth: 0 }}>
            <ContentCard className="d-flex flex-1 flex-column gap-m">
              <WrittenFeedback
                gradedAt={props.scoreReport.finishedAt}
                grader={{
                  name: props.scoreReport.grader.name,
                  type: props.scoreReport.grader.type
                }}
                narrative={renderTextAsParagraphs(props.scoreReport.content?.narrative)}
                sections={props.scoreReport.content?.sections || []}
                status={props.scoreReport.status}
              />
            </ContentCard>
          </div>
        </CardLayout.Card>

       <CardLayout.Card className="d-flex flex-column flex-md-row gap-m gap-lg-xl">
          <SectionHeader
            subtitle={
              <div className="d-flex flex-column gap-m">
                <div className="small">
                  This experimental feature uses AI to offer ideas for how to improve your score by about 1 point. You may review the rationale for each edit by selecting filters.
                </div>

                {
                  props.scoreReport.content?.editedResponse && (
                    <SuggestedEditFilters
                      activeFilters={suggestedEditFilters}
                      filters={props.scoreReport.content.editedResponse?.filters || []}
                      onChange={(filters) => setSuggestedEditFilters(filters)}
                    />
                  )
                }
              </div>
            }
            supertitle="Suggested edits"
            supertitleIcon="pencil-square"
            title="How to improve your score"
          />

          <div className="d-flex flex-1 flex-column gap-m" style={{ minWidth: 0 }}>
            {
              props.scoreReport.content?.editedResponse && (
                <InfoAlert>
                  We've made edits to offer ideas for how to improve your score by about 1 point.
                </InfoAlert>
              )
            }

            <ContentCard className="d-flex flex-1 flex-column gap-m">
              <SuggestedEdits
                activeFilters={suggestedEditFilters}
                editedResponse={props.scoreReport.content?.editedResponse}
                examName={props.branding.examName}
                filters={props.scoreReport.content?.editedResponse?.filters || []}
                gradedAt={props.scoreReport.finishedAt}
                onChangeFilters={setSuggestedEditFilters}
                score={props.scoreReport.score}
                status={props.scoreReport.status}
              />
            </ContentCard>
          </div>
        </CardLayout.Card>

        <div className="d-flex flex-column gap-m u-margin-T-m">
          <div className="d-flex flex-column gap-s gap-lg-m">

            <div className="align-items-center d-flex gap-s">
              <h3 className="u-margin-A-n"><strong>Tell Us How We Did</strong></h3>
            </div>

            <hr className="u-margin-A-n" />
          </div>

          <RatingForm
            rating={props.rating}
            rateableName="score report and feedback"
          />
        </div>

        {
          props.relatedLessons.length > 0 && (
            <div className="d-flex flex-column gap-m u-margin-T-m">
              <div className="d-flex flex-column gap-s gap-lg-m">

                <div className="align-items-center d-flex gap-s">
                  <h3 className="u-margin-A-n"><strong>Lessons</strong></h3>

                  <div style={{ fontSize: 18}}>
                    ({lessonsWatchedCount()} of {props.relatedLessons.length} watched)
                  </div>
                </div>

                <hr className="u-margin-A-n" />
              </div>

              <div className="d-flex flex-column gap-m">
                {
                  props.relatedLessons.map((lesson) => (
                    <CardLayout.Card className="flex-1">
                      <LessonActivity {...lesson} />
                    </CardLayout.Card>
                  ))
                }
              </div>
            </div>
          )
        }
      </div>
    </CardLayout>
  );
};

export default WritingScoreReport;
